/**
 * Notifications and toasts.
 *
 * Unifies the three legacy notification systems behind one provider. The scope
 * (global / patient / doctor) is derived from the session, so a component just
 * calls `notify(...)` and the entry lands in the right bucket and the right
 * storage key.
 *
 * المرحلة 1.2 (F07): for a signed-in person the bell lives on the server
 * (/api/notifications). The list is read from there, a read or a removal is
 * written there, and the server pushes every change to the person's other
 * screens over the live socket (hooks/useNotifyChannel.js), so the same
 * notification is read on the phone and on the computer. What this browser
 * kept before is sent to the server once, the first time it opens. Toasts stay
 * as they were, and a server that does not answer leaves the browser's own list
 * working exactly as before.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  addNotification,
  createNotification,
  markAllRead,
  markRead,
  notificationIcon,
  notificationStorageKey,
  removeNotification,
  unreadCount,
  NOTIFICATION_TYPES,
} from '../domain/notifications';
import { notificationApi } from '../api/endpoints';
import { useNotifyChannel } from '../hooks/useNotifyChannel';
import { ROLES, useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const TOAST_DURATION_MS = 4000;

function readStored(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeStored(key, list) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* quota exceeded — notifications are non-critical, so drop silently */
  }
}

/** A row the server wrote has a numeric id; one only this screen holds does not. */
const isServerId = (id) => /^\d+$/.test(String(id));

/** The browser's list went to the server once: this marks it, per storage key. */
const importedKey = (storageKey) => `${storageKey}:server`;

function wasImported(storageKey) {
  try {
    return localStorage.getItem(importedKey(storageKey)) === '1';
  } catch {
    return false;
  }
}

function markImported(storageKey) {
  try {
    localStorage.setItem(importedKey(storageKey), '1');
  } catch {
    /* storage unavailable: the server ignores a second copy anyway */
  }
}

/** The server's notification in the shape the panel reads. */
const fromServer = (n) => ({ ...n, icon: n.icon || notificationIcon(n.type) });

/** Which storage bucket the current session writes to. */
function scopeFor(session) {
  if (!session) return { scope: 'global', ownerId: null };
  if (session.role === ROLES.PATIENT) return { scope: 'patient', ownerId: session.patientId };
  if (session.role === ROLES.DOCTOR) return { scope: 'doctor', ownerId: session.doctorId ?? session.id };
  if (session.role === ROLES.TEAM_MEMBER) return { scope: 'team', ownerId: session.teamMemberId };
  return { scope: 'global', ownerId: null };
}

export function NotificationProvider({ children }) {
  const { session, isAuthenticated } = useAuth();
  const { scope, ownerId } = useMemo(() => scopeFor(session), [session]);
  const storageKey = useMemo(() => notificationStorageKey(scope, ownerId), [scope, ownerId]);

  const [notifications, setNotifications] = useState(() => readStored(storageKey));
  const [toasts, setToasts] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  // True once the server answered: the list is the server's from then on.
  const [live, setLive] = useState(false);
  const liveRef = useRef(false);
  liveRef.current = live;
  const listRef = useRef(notifications);
  listRef.current = notifications;
  const toastTimers = useRef(new Map());

  // Swap the list when the signed-in identity changes.
  useEffect(() => {
    setNotifications(readStored(storageKey));
  }, [storageKey]);

  // Persist on every change so a reload keeps the list — while the browser's
  // own list is the one in use.
  useEffect(() => {
    if (!live) writeStored(storageKey, notifications);
  }, [live, storageKey, notifications]);

  // Another tab writing the same key keeps this one in step — the legacy
  // `handleStorageChange`, reduced to the part that actually mattered.
  useEffect(() => {
    if (live) return undefined;
    const onStorage = (event) => {
      if (event.key === storageKey) setNotifications(readStored(storageKey));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [live, storageKey]);

  /** The server's list; the browser's stays in use when the server does not answer. */
  const load = useCallback(async () => {
    try {
      const answer = await notificationApi.list();
      setNotifications((answer.notifications || []).map(fromServer));
      setLive(true);
      return true;
    } catch (err) {
      console.warn('[notifications] The server list did not load; this browser keeps its own:', err.message);
      return false;
    }
  }, []);

  // A signed-in session: send this browser's old list once, then read the server's.
  useEffect(() => {
    if (!isAuthenticated) {
      setLive(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (!wasImported(storageKey)) {
        const kept = readStored(storageKey).filter((n) => n && n.id && (n.title || n.message));
        try {
          if (kept.length) await notificationApi.import(kept.slice(0, 100));
          markImported(storageKey);
        } catch (err) {
          // Tried again on the next open; the list below still loads.
          console.warn('[notifications] The browser’s list was not sent:', err.message);
        }
      }
      if (!cancelled) await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, storageKey, load]);

  useEffect(
    () => () => {
      toastTimers.current.forEach((timer) => clearTimeout(timer));
      toastTimers.current.clear();
    },
    [],
  );

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = toastTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      toastTimers.current.delete(id);
    }
  }, []);

  /** Transient banner. Does not persist. */
  const toast = useCallback(
    (message, type = NOTIFICATION_TYPES.INFO, duration = TOAST_DURATION_MS) => {
      const entry = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, message, type };
      setToasts((current) => [...current, entry]);
      const timer = setTimeout(() => dismissToast(entry.id), duration);
      toastTimers.current.set(entry.id, timer);
      return entry.id;
    },
    [dismissToast],
  );

  /** What the server pushes: a new one, read ones, removed ones; a reconnect re-reads the list. */
  const onLive = useCallback(
    (message) => {
      if (message.type === 'notify-ready' || message.type === 'poll') {
        load();
        return;
      }
      if (message.type === 'notification' && message.notification) {
        const incoming = fromServer(message.notification);
        const known = listRef.current.some((n) => n.id === incoming.id);
        setNotifications((current) => (current.some((n) => n.id === incoming.id) ? current : addNotification(current, incoming)));
        // An event elsewhere (a booking confirmed, a result back) shows as it
        // arrives; what this screen rang itself already did, if it asked to.
        if (!known && incoming.topic && incoming.topic !== 'system') toast(incoming.title || incoming.message, incoming.type);
        return;
      }
      if (message.type === 'notifications-read') {
        setNotifications((current) =>
          message.ids === 'all' ? markAllRead(current) : current.map((n) => (message.ids.includes(n.id) ? { ...n, read: true } : n)),
        );
        return;
      }
      if (message.type === 'notifications-removed' && Array.isArray(message.ids)) {
        setNotifications((current) => current.filter((n) => !message.ids.includes(n.id)));
      }
    },
    [load, toast],
  );

  useNotifyChannel(live, onLive);

  /** Persisted notification, optionally mirrored as a toast. */
  const notify = useCallback(
    ({ type = NOTIFICATION_TYPES.INFO, title, message, action = null, icon = null, showToast = false, topic = undefined, sourceKey = undefined }) => {
      const entry = createNotification({ type, title, message, action, icon });
      setNotifications((current) => addNotification(current, entry));
      if (showToast) toast(title || message, type);
      if (liveRef.current) {
        notificationApi
          .ring({ type, title, message, action, icon, topic, sourceKey })
          .then(({ notification }) => {
            // The server's row takes the place of this screen's copy; a topic the
            // person turned off is not kept (the toast, if asked for, was shown).
            setNotifications((current) => {
              const rest = current.filter((n) => n.id !== entry.id && (!notification || n.id !== notification.id));
              return notification ? addNotification(rest, fromServer(notification)) : rest;
            });
          })
          .catch((err) => console.warn('[notifications] Kept on this screen only:', err.message));
      }
      return entry;
    },
    [toast],
  );

  const markAsRead = useCallback((id) => {
    setNotifications((current) => markRead(current, id));
    if (liveRef.current && isServerId(id)) notificationApi.read(id).catch(() => {});
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((current) => markAllRead(current));
    if (liveRef.current) notificationApi.readAll().catch(() => {});
  }, []);

  const remove = useCallback((id) => {
    setNotifications((current) => removeNotification(current, id));
    if (liveRef.current && isServerId(id)) notificationApi.remove(id).catch(() => {});
  }, []);

  const clearAll = useCallback(() => {
    const held = listRef.current;
    setNotifications([]);
    if (liveRef.current) Promise.all(held.filter((n) => isServerId(n.id)).map((n) => notificationApi.remove(n.id))).catch(() => {});
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      unread: unreadCount(notifications),
      panelOpen,
      setPanelOpen,
      togglePanel: () => setPanelOpen((open) => !open),

      notify,
      markAsRead,
      markAllAsRead,
      remove,
      clearAll,
      live,

      toasts,
      toast,
      success: (message) => toast(message, NOTIFICATION_TYPES.SUCCESS),
      error: (message) => toast(message, NOTIFICATION_TYPES.DANGER, 6000),
      warn: (message) => toast(message, NOTIFICATION_TYPES.WARNING),
      dismissToast,
    }),
    [notifications, panelOpen, notify, markAsRead, markAllAsRead, remove, clearAll, live, toasts, toast, dismissToast],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used inside a <NotificationProvider>');
  return context;
}
